import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Spinner } from '@/components/Spinner'
import { ToastProvider } from '@/components/Toast'
import { SessionProvider, useSession } from '@/features/auth/SessionProvider'
import { SignInScreen } from '@/features/auth/SignInScreen'
import { PatientFormScreen } from '@/features/patient/PatientFormScreen'
import { PatientScreen } from '@/features/patient/PatientScreen'
import { AppointmentsScreen } from '@/features/insights/AppointmentsScreen'
import { DashboardScreen } from '@/features/insights/DashboardScreen'
import { PrescriptionsScreen } from '@/features/insights/PrescriptionsScreen'
import { SearchScreen } from '@/features/search/SearchScreen'
import { SettingsScreen } from '@/features/settings/SettingsScreen'
import { VisitEditRoute } from '@/features/visit/VisitEditRoute'
import { CallersScreen } from '@/features/callers/CallersScreen'
import { CallerFormScreen } from '@/features/callers/CallerFormScreen'
import { ConvertCallerScreen } from '@/features/callers/ConvertCallerScreen'
import { TravelScreen } from '@/features/travel/TravelScreen'
import { CalendarScreen } from '@/features/calendar/CalendarScreen'
import { ModalProvider } from './modals/ModalHost'
import { AppShell } from './AppShell'
import { NotFoundScreen } from './NotFoundScreen'
import { ThemeProvider } from './ThemeProvider'
import { ErrorBoundary } from './ErrorBoundary'
import { createQueryClient } from './queryClient'

function Routed() {
  const { user, loading } = useSession()

  if (loading) return <Spinner label="Loading" />
  if (!user) return <SignInScreen />

  return (
    <AppShell>
      <Routes>
        {/* One error boundary per route, not one for the whole app: a failure
            in the history list must not blank the visit being typed. */}
        <Route
          path="/"
          element={
            <ErrorBoundary label="dashboard">
              <DashboardScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/patients"
          element={
            <ErrorBoundary label="patients">
              <SearchScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/appointments"
          element={
            <ErrorBoundary label="appointments">
              <AppointmentsScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/prescriptions"
          element={
            <ErrorBoundary label="prescriptions">
              <PrescriptionsScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/patients/new"
          element={
            <ErrorBoundary label="patient-form">
              <PatientFormScreen mode="create" />
            </ErrorBoundary>
          }
        />
        <Route
          path="/patients/:patientId"
          element={
            <ErrorBoundary label="patient">
              <PatientScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/patients/:patientId/edit"
          element={
            <ErrorBoundary label="patient-form">
              <PatientFormScreen mode="edit" />
            </ErrorBoundary>
          }
        />
        <Route
          path="/patients/:patientId/visits/new"
          element={
            <ErrorBoundary label="consultation">
              <PatientScreen initialTab="consultation" />
            </ErrorBoundary>
          }
        />
        <Route
          path="/visits/:visitId/edit"
          element={
            <ErrorBoundary label="consultation">
              <VisitEditRoute />
            </ErrorBoundary>
          }
        />
        <Route
          path="/callers"
          element={
            <ErrorBoundary label="callers">
              <CallersScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/callers/new"
          element={
            <ErrorBoundary label="caller-form">
              <CallerFormScreen mode="create" />
            </ErrorBoundary>
          }
        />
        {/* Declared BEFORE /:callerId, or "new" is parsed as a caller id. */}
        <Route
          path="/callers/:callerId"
          element={
            <ErrorBoundary label="caller-form">
              <CallerFormScreen mode="edit" />
            </ErrorBoundary>
          }
        />
        <Route
          path="/callers/:callerId/convert"
          element={
            <ErrorBoundary label="caller-convert">
              <ConvertCallerScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/travel"
          element={
            <ErrorBoundary label="travel">
              <TravelScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/calendar"
          element={
            <ErrorBoundary label="calendar">
              <CalendarScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path="/settings"
          element={
            <ErrorBoundary label="settings">
              <SettingsScreen />
            </ErrorBoundary>
          }
        />
        {/* An unknown URL says so rather than redirecting to the dashboard:
            a stale patient link that silently lands on the home screen looks
            like it worked. */}
        <Route
          path="*"
          element={
            <ErrorBoundary label="not-found">
              <NotFoundScreen />
            </ErrorBoundary>
          }
        />
      </Routes>
    </AppShell>
  )
}

export function App() {
  const queryClient = useMemo(() => createQueryClient(), [])
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
        <ToastProvider>
          <SessionProvider>
            <ModalProvider>
              <Routed />
            </ModalProvider>
          </SessionProvider>
        </ToastProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
